import Typography from '@mui/material/Typography'
import type { FC } from 'react'
import React, { useState, useContext } from 'react'
import styled from 'styled-components'

import ctx from '@/context/AppContext'
import type { FetchSingleApplication } from '@/hooks/useSubject'
import type { Application } from '@/types/Activity'
import { REDUCED_JOURNEY_SUBJECTS } from '@/utils/constants'
import { TENANT_SUBJECTS } from '@/utils/constants'
import { Colours } from '@/utils/styles'
import { isGuarantor } from '@/utils/subjectHelpers'

import AdditionalSubjects from './AdditionalSubjects'
import DangerZone from './DangerZone'
import UpdatePriceSharePanel from './EntityPanels/PriceShare/UpdatePriceSharePanel'
import ExpandeableCard from './ExpandableCard'

const Body = styled.div`
  margin-top: 15px;
  flex: 1;
  display: flex;
  flex-flow: column;

  h2 {
    font-size: 14px;
  }
`

const Asset = styled.div`
  margin: 10px 0;

  span {
    text-transform: capitalize;
  }

  strong {
    margin-bottom: 10px;
    display: inline-block;
  }
`

const Action = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  img {
    margin-left: 5px;
  }

  &:hover {
    cursor: pointer;
  }
`

const DecisionControlContent = styled.div`
  flex: 1;
  h2 {
    margin-bottom: 0;
  }
`

const Sections = styled.div`
  padding-bottom: 20px;
  align-items: end;

  h5 {
    margin: 0;
    color: ${Colours.lightGray};
  }
  @media (max-width: 1200px) {
    min-height: 20px;
  }
`

const SectionIcon = styled.img`
  height: 16px;
  width: 16px;
  margin-left: 20px;
`

interface Props {
  type?: string
  applicationId: string
  subjectId: string
  agencyName?: string
  onClose: Function
  application: Application
  fetchSingleApplication: FetchSingleApplication
  alwaysExpand?: boolean
  isRestrictedReference?: boolean
}

const DetailsCard: FC<Props> = ({
  type,
  applicationId,
  subjectId,
  agencyName,
  children,
  onClose,
  application,
  fetchSingleApplication,
  alwaysExpand = false,
  isRestrictedReference = false,
}) => {
  const [updatingPriceShare, setUpdatingPriceShare] = useState(false)
  const [isExpanded, setIsExpanded] = useState(alwaysExpand)

  const { personId } = useContext(ctx)
  const canEditPriceShare = !isRestrictedReference && !isGuarantor(application.type)

  return (
    <ExpandeableCard
      label="Details"
      icon="tenant"
      isExpanded={isExpanded}
      handleExpand={alwaysExpand ? undefined : () => setIsExpanded(!isExpanded)}
      expandedContent={
        <Body>
          {type}
          <h5>Tenancy ID (2.0 Reference)</h5>
          {applicationId}
          <h5>Applicant ID (2.0 Reference)</h5>
          {subjectId}
          <h5>Goodlord Client</h5>
          {agencyName}
          <h5>Agent contact details</h5>
          {application.agent ? (
            <>
              <div>{application.agent.name}</div>
              <div>{application.agent.phone}</div>
            </>
          ) : (
            <>Unknown</>
          )}
          {children}

          {updatingPriceShare && (
            <UpdatePriceSharePanel
              fetchSingleApplication={fetchSingleApplication}
              setOpen={setUpdatingPriceShare}
              initialPrice={application.subjectAssets![0].attributes.priceShare}
              assetPrice={application.assets![0].attributes.price}
              subjectData={{
                subjectId: application.subjectId,
                applicationId: application.applicationId,
                customerId: application.customerId,
              }}
            />
          )}

          {!REDUCED_JOURNEY_SUBJECTS.includes(application.type) && (
            <>
              <Asset>
                {application.assets &&
                  application.assets !== null &&
                  application.assets.length > 0 && (
                    <>
                      {application.subjectAssets &&
                        application.subjectAssets.length > 0 && (
                          <>
                            <h5>Price share</h5>
                            <div className="side-by-side-flex">
                              {`£${application.subjectAssets[0].attributes.priceShare}`}{' '}
                              {canEditPriceShare && (
                                <Action onClick={() => setUpdatingPriceShare(true)}>
                                  <img
                                    src="/assets/svg/edit.svg"
                                    alt="Edit price share"
                                  />
                                </Action>
                              )}
                            </div>
                            {isGuarantor(application.type) && (
                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{ mt: 0.5 }}
                              >
                                Worked out automatically from the tenants this guarantor
                                covers. To change it, edit their price shares in Linked
                                people below.
                              </Typography>
                            )}
                          </>
                        )}
                    </>
                  )}
              </Asset>
            </>
          )}

          <>
            <DecisionControlContent>
              {application.attachedSubjects &&
                application.attachedSubjects.length > 0 && (
                  <>
                    <h2>Linked People</h2>
                    <Sections>
                      <AdditionalSubjects people={application.attachedSubjects} />
                    </Sections>
                  </>
                )}

              {TENANT_SUBJECTS.includes(application.type) &&
                application.additionalTenants &&
                application.additionalTenants.length > 0 && (
                  <>
                    <h2>Other Tenants</h2>
                    <Sections className="side-by-side-flex">
                      <AdditionalSubjects people={application.additionalTenants} />
                    </Sections>
                  </>
                )}

              {!REDUCED_JOURNEY_SUBJECTS.includes(application.type) && (
                <>
                  <h2>Advisories</h2>

                  <Sections className="side-by-side-flex">
                    {application?.advisories && application?.advisories.length > 0 ? (
                      <div>
                        {application?.advisories.map((advisory: any, i) => (
                          <div className="side-by-side-flex advisory-item" key={i}>
                            <p>{advisory.description}</p>
                            <SectionIcon
                              src="/assets/svg/attention.svg"
                              alt=""
                            ></SectionIcon>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>Looking good for now!</p>
                    )}
                  </Sections>
                </>
              )}
            </DecisionControlContent>
            {!REDUCED_JOURNEY_SUBJECTS.includes(application.type) &&
              !isRestrictedReference && (
                <DangerZone
                  application={application}
                  onCancelSuccess={onClose}
                  personId={personId}
                />
              )}
          </>
        </Body>
      }
    />
  )
}

export default DetailsCard
